import { Sparkles, Target, Heart, Users, Award, BookOpen, Play } from "lucide-react";
import { schoolInfo, videos } from "../mock/mock";
import Testimonials from "../components/sections/Testimonials";
import CTA from "../components/sections/CTA";
import ValuesInfographic from "../components/infographics/ValuesInfographic";
import JourneyInfographic from "../components/infographics/JourneyInfographic";

const PageHero = ({ title, subtitle, kicker }) => (
  <section className="pt-16 pb-20 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 relative overflow-hidden">
    <div className="absolute top-10 right-10 w-32 h-32 bg-yellow-300/40 blob-shape animate-float hidden md:block" />
    <div className="absolute bottom-10 left-10 w-24 h-24 bg-orange-300/40 rounded-full animate-float hidden md:block" style={{ animationDelay: "1s" }} />
    <div className="container mx-auto px-4 relative z-10 text-center">
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-orange-100 mb-4">
        <Sparkles className="w-4 h-4 text-orange-600" />
        <span className="text-xs font-semibold text-orange-700 tracking-widest uppercase">{kicker}</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-bold text-stone-800 mb-4">{title}</h1>
      <p className="text-stone-600 max-w-2xl mx-auto">{subtitle}</p>
    </div>
  </section>
);

const AboutPage = () => {
  const values = [
    { icon: Heart, title: "Compassion", desc: "Nurturing empathy and kindness in every child through daily interactions." },
    { icon: BookOpen, title: "Wisdom", desc: "Blending Vedic knowledge with modern academics for holistic minds." },
    { icon: Users, title: "Community", desc: "Building a family-like environment where children thrive together." },
    { icon: Award, title: "Excellence", desc: "Pursuing the highest standards in early childhood education." }
  ];

  return (
    <div>
      <PageHero
        kicker="About Us"
        title="Our Story of Sanskaar & Smart Learning"
        subtitle="An initiative by Aarohi Welfare Foundation to shape rooted, curious and confident children."
      />

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_gayanam-preschool/artifacts/e65ire9l_ac10deea-9fd0-4408-9590-5d814b3f88bf.png" alt="About Aadi Gayanam Play School" className="rounded-3xl shadow-xl w-full" />
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-800 mb-5">
                Where <span className="text-orange-600">Tradition</span> Meets <span className="text-amber-600">Tomorrow</span>
              </h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                Founded in <strong>{schoolInfo.established}</strong>, Aadi Gayanam Play School emerged from a simple yet powerful vision — to raise a generation of children who are firmly rooted in Bharatiya sanskaar while being fully prepared for the modern world.
              </p>
              <p className="text-stone-600 leading-relaxed mb-4">
                As an initiative of the <strong>{schoolInfo.parentOrg}</strong> in Jaipur, our approach combines the timeless wisdom of Sanatan Dharma — shlokas, yoga, storytelling from Ramayana and Mahabharata, and sattvik values — with globally-benchmarked early childhood pedagogies.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Every day at Aadi Gayanam is a celebration of curiosity, culture and creativity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder's Message Video */}
      <section className="py-16 bg-gradient-to-br from-stone-900 via-orange-950 to-stone-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-yellow-300/30 mb-4">
              <Play className="w-4 h-4 text-yellow-300" />
              <span className="text-xs font-semibold text-yellow-200 tracking-widest uppercase">Founder's Message</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">
              Watch Our <span className="text-yellow-300">Vision Come Alive</span>
            </h2>
            <p className="text-orange-100">Meet the minds behind Aadi Gayanam and hear the story of how we're building India's most unique play school.</p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="aspect-video rounded-3xl overflow-hidden shadow-2xl border border-yellow-300/20">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${videos[0].youtubeId}?rel=0&modestbranding=1`}
                title="Aadi Gayanam Founder's Message"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 pattern-warm">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-3xl shadow-lg border border-orange-100">
              <div className="w-14 h-14 rounded-2xl bg-orange-500 flex items-center justify-center mb-4 shadow-lg">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-display font-bold text-2xl text-stone-800 mb-3">Our Vision</h3>
              <p className="text-stone-600 leading-relaxed">
                To create a generation of children who are grounded in Sanatan values, brimming with curiosity, and equipped with the skills to shape a better tomorrow.
              </p>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-lg border border-amber-100">
              <div className="w-14 h-14 rounded-2xl bg-amber-500 flex items-center justify-center mb-4 shadow-lg">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-display font-bold text-2xl text-stone-800 mb-3">Our Mission</h3>
              <p className="text-stone-600 leading-relaxed">
                To provide a safe, joyful and stimulating environment where every child discovers their unique potential through a balanced blend of tradition and innovation.
              </p>
            </div>
          </div>
        </div>
      </section>

      <ValuesInfographic />
      <JourneyInfographic />

      <Testimonials />
      <CTA />
    </div>
  );
};

export default AboutPage;
